import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useActiveSection } from '../../hooks/useActiveSection';
import '../LandingPage.css';

const SECTION_LINKS = [
  { id: 'services', label: 'Services' },
  { id: 'process', label: 'How we work' },
  { id: 'faq', label: 'FAQ' },
] as const;

// Every landing section in page order: sections without a nav link are
// tracked too, so no link stays highlighted while they are on screen.
const SECTION_IDS = ['chatserve', 'services', 'promise', 'about', 'process', 'faq', 'contact'];

// Elements behind the open mobile menu that must leave the tab order.
const BEHIND_MENU = '#main-content, .footer, .chat-widget-icon, .chat-widget-window';

/**
 * Floating "island" header: a solid dark pill detached from the top of the
 * page. It needs no scroll listener because it reads the same over the dark
 * hero and over light content.
 */
function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const onLanding = location.pathname === '/';
  const activeSection = useActiveSection(SECTION_IDS, onLanding);

  // Any navigation closes the mobile menu.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.key]);

  // While the menu is open: Escape closes it (focus back to the toggle) and
  // the page behind it is inert, so Tab stays inside the menu.
  useEffect(() => {
    if (!isMenuOpen) return;
    const behind = Array.from(document.querySelectorAll<HTMLElement>(BEHIND_MENU));
    behind.forEach((el) => el.setAttribute('inert', ''));

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        burgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      behind.forEach((el) => el.removeAttribute('inert'));
    };
  }, [isMenuOpen]);

  const sectionLinks = (className: string) =>
    SECTION_LINKS.map((link, index) => {
      const isActive = onLanding && activeSection === link.id;
      return (
        <Link
          key={link.id}
          to={`/#${link.id}`}
          className={`${className} ${isActive ? 'active' : ''}`}
          aria-current={isActive ? 'location' : undefined}
          style={{ '--i': index } as CSSProperties}
        >
          {link.label}
        </Link>
      );
    });

  const productsLink = (className: string, index: number) => (
    <NavLink
      to="/products"
      className={({ isActive }) => `${className} ${isActive ? 'active' : ''}`}
      style={{ '--i': index } as CSSProperties}
    >
      Products
    </NavLink>
  );

  return (
    <header className={`header ${isMenuOpen ? 'header--menu-open' : ''}`}>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      <div className="header-pill">
        <Link to="/" className="logo">
          EF<span>Soft</span>
        </Link>

        <nav className="header-nav" aria-label="Main">
          {sectionLinks('nav-link')}
          {productsLink('nav-link', SECTION_LINKS.length)}
        </nav>

        <Link to="/#contact" className="nav-cta">
          Start a project
        </Link>

        <button
          ref={burgerRef}
          type="button"
          className={`burger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen((v) => !v)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <span></span>
          <span></span>
        </button>
      </div>

      <div id="mobile-menu" className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-menu-nav" aria-label="Main">
          {sectionLinks('mobile-menu-link')}
          {productsLink('mobile-menu-link', SECTION_LINKS.length)}
          <Link
            to="/#contact"
            className="mobile-menu-cta"
            style={{ '--i': SECTION_LINKS.length + 1 } as CSSProperties}
          >
            Start a project
          </Link>
        </nav>
      </div>
    </header>
  );
}

export default Header;
